import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, Send, X, Bot, User } from 'lucide-react';

interface ChatMessage {
  id: string;
  type: 'user' | 'bot';
  content: string;
  timestamp: Date;
  isTyping?: boolean;
}

interface ChatInterfaceProps {
  selectedStock: string;
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({ selectedStock }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: '1',
      type: 'bot',
      content: `Hello! I'm your AI Stock Analyst. I can help you analyze ${selectedStock || 'stocks'}, provide market insights, and answer investment questions. Try asking me something like "Is ${selectedStock || 'AAPL'} a good buy?" or "What's the market sentiment for tech stocks?"`,
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Mock AI responses based on common queries
  const generateAIResponse = (userMessage: string): string => {
    const message = userMessage.toLowerCase();
    
    if (message.includes('buy') || message.includes('purchase')) {
      return `Based on current technical analysis for ${selectedStock}:

📈 **Technical Indicators:**
- RSI: 58.4 (Neutral zone)
- MACD: Bullish crossover detected
- Moving averages showing upward trend

💡 **Recommendation:** MODERATE BUY
- Entry point: Current levels look favorable
- Stop loss: Consider setting at $${(Math.random() * 20 + 160).toFixed(2)}
- Target: $${(Math.random() * 30 + 200).toFixed(2)}

⚠️ **Risk Assessment:** Medium risk due to market volatility. Consider position sizing and diversification.

*This is not financial advice. Please consult with a qualified financial advisor.*`;
    }
    
    if (message.includes('sell') || message.includes('exit')) {
      return `Analyzing exit strategy for ${selectedStock}:

📊 **Current Position Analysis:**
- Recent performance: Mixed signals
- Volume trends: Decreasing momentum
- Support levels: Key support at $${(Math.random() * 20 + 150).toFixed(2)}

🎯 **Exit Strategy Options:**
1. **Partial Exit:** Consider taking 30-50% profits
2. **Trailing Stop:** Set at 8-10% below current price
3. **Time-based:** Hold for earnings announcement

📉 **Risk Factors:** Watch for break below key support levels.`;
    }
    
    if (message.includes('sentiment') || message.includes('news')) {
      return `Current market sentiment for ${selectedStock}:

📰 **News Sentiment:** 65% Bullish
- Recent earnings beat expectations
- Positive analyst upgrades
- Strong sector performance

🤖 **AI Sentiment Score:** 7.2/10
- Social media mentions: Trending positive
- Institutional activity: Net buying
- Options flow: Bullish bias

📈 **Overall Assessment:** Cautiously optimistic with strong fundamentals supporting current levels.`;
    }
    
    if (message.includes('price target') || message.includes('forecast')) {
      return `Price analysis and targets for ${selectedStock}:

🎯 **Price Targets:**
- **Conservative:** $${(Math.random() * 20 + 180).toFixed(2)} (3-month)
- **Moderate:** $${(Math.random() * 40 + 200).toFixed(2)} (6-month)
- **Aggressive:** $${(Math.random() * 60 + 220).toFixed(2)} (12-month)

📊 **Key Levels:**
- **Resistance:** $${(Math.random() * 15 + 195).toFixed(2)}
- **Support:** $${(Math.random() * 15 + 175).toFixed(2)}

⚡ **Catalysts:** Upcoming earnings, product launches, and market conditions will be key drivers.`;
    }
    
    if (message.includes('risk') || message.includes('volatility')) {
      return `Risk assessment for ${selectedStock}:

⚠️ **Risk Level:** MEDIUM

🔍 **Risk Factors:**
- Market volatility: 25% (elevated)
- Sector rotation risk: Moderate
- Earnings uncertainty: Low
- Regulatory risk: Low

🛡️ **Risk Management:**
- Position size: Max 5% of portfolio
- Stop loss: 15% below entry
- Diversification: Essential

📈 **Risk/Reward:** Favorable with proper risk management in place.`;
    }
    
    // Default response
    return `I understand you're asking about ${selectedStock}. Here's what I can help you with:

🔍 **Available Analysis:**
- Technical indicators (RSI, MACD, Moving Averages)
- Price targets and forecasts
- Market sentiment analysis
- Risk assessment
- Buy/Sell recommendations

💬 **Try asking:**
- "Should I buy ${selectedStock}?"
- "What's the price target for ${selectedStock}?"
- "What are the risks of investing in ${selectedStock}?"
- "What's the market sentiment for ${selectedStock}?"

How can I assist you with your investment analysis?`;
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: inputValue,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    // Simulate AI thinking time
    setTimeout(() => {
      const aiResponse: ChatMessage = {
        id: (Date.now() + 1).toString(),
        type: 'bot',
        content: generateAIResponse(inputValue),
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, aiResponse]);
      setIsTyping(false);
    }, 1500 + Math.random() * 1000);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <>
      {/* Chat Toggle Button */}
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-6 right-6 p-4 bg-neon-blue hover:bg-neon-blue/80 text-black rounded-full shadow-lg transition-all duration-300 z-40 ${
          isOpen ? 'scale-0' : 'scale-100'
        }`}
      >
        <MessageCircle size={24} />
      </button>

      {/* Chat Interface */}
      <div className={`fixed bottom-6 right-6 w-96 h-[500px] bg-terminal-surface border border-terminal-border rounded-lg shadow-2xl transition-all duration-300 z-50 ${
        isOpen ? 'scale-100 opacity-100' : 'scale-0 opacity-0'
      }`}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-terminal-border">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-neon-blue/20 rounded-lg">
              <Bot className="text-neon-blue" size={20} />
            </div>
            <div>
              <h3 className="font-semibold text-terminal-text">AI Stock Analyst</h3>
              <p className="text-xs text-terminal-muted">
                {isTyping ? 'Analyzing...' : 'Online'}
              </p>
            </div>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="p-1 hover:bg-terminal-border rounded text-terminal-muted hover:text-terminal-text transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 h-[340px] scrollbar-thin">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex gap-3 ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              {message.type === 'bot' && (
                <div className="flex-shrink-0 w-8 h-8 bg-neon-blue/20 rounded-full flex items-center justify-center">
                  <Bot size={16} className="text-neon-blue" />
                </div>
              )}
              
              <div className={`max-w-[80%] ${message.type === 'user' ? 'order-1' : ''}`}>
                <div className={`p-3 rounded-lg ${
                  message.type === 'user'
                    ? 'bg-neon-blue text-black'
                    : 'bg-terminal-bg border border-terminal-border text-terminal-text'
                }`}>
                  <div className="text-sm whitespace-pre-wrap leading-relaxed">
                    {message.content}
                  </div>
                </div>
                <div className={`text-xs text-terminal-muted mt-1 ${
                  message.type === 'user' ? 'text-right' : 'text-left'
                }`}>
                  {formatTime(message.timestamp)}
                </div>
              </div>

              {message.type === 'user' && (
                <div className="flex-shrink-0 w-8 h-8 bg-terminal-border rounded-full flex items-center justify-center order-2">
                  <User size={16} className="text-terminal-text" />
                </div>
              )}
            </div>
          ))}
          
          {isTyping && (
            <div className="flex gap-3 justify-start">
              <div className="flex-shrink-0 w-8 h-8 bg-neon-blue/20 rounded-full flex items-center justify-center">
                <Bot size={16} className="text-neon-blue" />
              </div>
              <div className="bg-terminal-bg border border-terminal-border rounded-lg p-3">
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-terminal-muted rounded-full animate-bounce"></div>
                  <div className="w-2 h-2 bg-terminal-muted rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                  <div className="w-2 h-2 bg-terminal-muted rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="p-4 border-t border-terminal-border">
          <div className="flex gap-2">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={`Ask about ${selectedStock || 'stocks'}...`}
              className="flex-1 px-3 py-2 bg-terminal-bg border border-terminal-border rounded-lg text-terminal-text placeholder-terminal-muted focus:outline-none focus:border-neon-blue text-sm"
              disabled={isTyping}
            />
            <button
              onClick={handleSendMessage}
              disabled={!inputValue.trim() || isTyping}
              className="p-2 bg-neon-blue hover:bg-neon-blue/80 disabled:bg-terminal-border disabled:text-terminal-muted text-black rounded-lg transition-colors"
            >
              <Send size={16} />
            </button>
          </div>
          
          {/* Quick Actions */}
          <div className="flex gap-2 mt-2">
            <button
              onClick={() => setInputValue(`Is ${selectedStock} a good buy?`)}
              className="text-xs px-2 py-1 bg-terminal-border hover:bg-terminal-muted/20 text-terminal-muted rounded transition-colors"
            >
              Buy Analysis
            </button>
            <button
              onClick={() => setInputValue(`What's the price target for ${selectedStock}?`)}
              className="text-xs px-2 py-1 bg-terminal-border hover:bg-terminal-muted/20 text-terminal-muted rounded transition-colors"
            >
              Price Target
            </button>
            <button
              onClick={() => setInputValue(`Market sentiment for ${selectedStock}`)}
              className="text-xs px-2 py-1 bg-terminal-border hover:bg-terminal-muted/20 text-terminal-muted rounded transition-colors"
            >
              Sentiment
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ChatInterface;